import { type Card, type Suit, cardStrength, cardSuit, pointsOf } from './cards.js';

/** Seats are 0..3 clockwise. Seats 0+2 are team 0, seats 1+3 are team 1. */
export type Seat = 0 | 1 | 2 | 3;
export type Team = 0 | 1;

export const SEATS: readonly Seat[] = [0, 1, 2, 3];

export function teamOf(seat: Seat): Team {
  return (seat % 2) as Team;
}

export function partnerOf(seat: Seat): Seat {
  return ((seat + 2) % 4) as Seat;
}

export function nextSeat(seat: Seat): Seat {
  return ((seat + 1) % 4) as Seat;
}

/** The two seats of the other team, clockwise from `seat`. */
export function opponentsOf(seat: Seat): readonly [Seat, Seat] {
  return [nextSeat(seat), nextSeat(partnerOf(seat))];
}

export interface TrickPlay {
  readonly seat: Seat;
  readonly card: Card;
}

export interface Trick {
  readonly leader: Seat;
  readonly plays: readonly TrickPlay[];
}

export interface CompletedTrick extends Trick {
  readonly plays: readonly TrickPlay[];
  readonly winner: Seat;
  readonly points: number;
  /** 1-based index within the deal. */
  readonly number: number;
}

export function emptyTrick(leader: Seat): Trick {
  return { leader, plays: [] };
}

export function seatToMove(trick: Trick): Seat {
  return ((trick.leader + trick.plays.length) % 4) as Seat;
}

export function ledSuit(trick: Trick): Suit | null {
  const first = trick.plays[0];
  return first ? cardSuit(first.card) : null;
}

/**
 * The play currently winning: highest trump if any trump has been played,
 * otherwise the highest card of the led suit. `trump` is `null` in a
 * *zonder troef* deal, where only the led suit can ever win.
 */
export function masterPlay(trick: Trick, trump: Suit | null): TrickPlay | null {
  const led = ledSuit(trick);
  if (led === null) return null;
  let best: TrickPlay | null = null;
  let bestScore = -1;
  for (const play of trick.plays) {
    const suit = cardSuit(play.card);
    if (suit !== led && suit !== trump) continue;
    // +8 lifts every trump above every plain card.
    const score = cardStrength(play.card) + (trump !== null && suit === trump ? 8 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = play;
    }
  }
  return best;
}

export function trickWinner(trick: Trick, trump: Suit | null): Seat {
  const best = masterPlay(trick, trump);
  if (!best) throw new Error('trickWinner: empty trick');
  return best.seat;
}

export function trickPoints(trick: Trick): number {
  return pointsOf(trick.plays.map((play) => play.card));
}

export function completeTrick(trick: Trick, trump: Suit | null, number: number): CompletedTrick {
  if (trick.plays.length !== 4) throw new Error('completeTrick: trick is not full');
  return {
    leader: trick.leader,
    plays: trick.plays,
    winner: trickWinner(trick, trump),
    points: trickPoints(trick),
    number,
  };
}
